<template>
  <!-- Поля обёрнуты в <label> — связь с контролом есть и без id -->
  <!-- eslint-disable vuejs-accessibility/label-has-for -->
  <section v-if="draft" class="w-branch-screen" aria-label="Режим работы филиала">
    <div class="w-branch-intro">
      <div>
        <h2>{{ store.currentBranch?.name }}</h2>
        <p>{{ store.actorDoctor?.name || 'Пользователь' }} · управление режимом</p>
      </div>
      <label class="w-field w-branch-effective">
        Действует с
        <input v-model.lazy="draft.effectiveFrom" type="date" @change="store.markBranchEdited()" />
      </label>
    </div>

    <div class="w-branch-reload-row">
      <p class="w-branch-period">{{ periodText }}</p>
      <button class="w-button w-ghost" type="button" @click="store.reloadBranch()">
        Загрузить сохранённый режим
      </button>
    </div>

    <div class="w-branch-key">
      <span>
        <q-icon name="apartment" />
        Работа филиала
      </span>
      <span>
        <q-icon name="medical_services" />
        Приём пациентов · внутри рабочего времени
      </span>
    </div>

    <div class="w-branch-section-head">
      <h3>Недельный режим</h3>
      <button class="w-button w-ghost" type="button" @click="store.copyMondayToWeekdays()">
        <q-icon name="content_copy" />
        Понедельник → будни
      </button>
    </div>
    <div class="w-branch-week">
      <details
        v-for="(day, index) in draft.week"
        :key="day.weekday"
        class="w-hours-detail"
        :open="!isMobile || expandedDays.has(index)"
        @toggle="onToggle(index, $event)"
      >
        <summary>
          <strong>{{ WEEKDAY_NAMES[day.weekday - 1] }}</strong>
          <span>{{ day.open.length ? `Приём ${hoursText(day.intake)}` : 'Выходной' }}</span>
        </summary>
        <BranchDayEditor kind="week" :index="index" :title="WEEKDAY_NAMES[day.weekday - 1]" />
      </details>
    </div>

    <section class="w-branch-exception-section" aria-label="Особые даты">
      <div class="w-branch-section-head">
        <h3>Особые даты</h3>
        <button class="w-button w-ghost" type="button" @click="store.addException()">
          <q-icon name="add" />
          Добавить дату
        </button>
      </div>
      <div class="w-branch-exceptions">
        <div v-for="(day, index) in draft.exceptions" :key="index" class="w-exception-card">
          <div class="w-exception-head">
            <label class="w-field">
              Дата
              <input v-model.lazy="day.date" type="date" @change="store.markBranchEdited()" />
            </label>
            <label class="w-field">
              Название
              <input
                v-model.lazy="day.label"
                type="text"
                maxlength="80"
                placeholder="Например, сокращённый день"
                @change="store.markBranchEdited()"
              />
            </label>
            <button
              class="w-button w-ghost w-icon"
              type="button"
              :aria-label="`Удалить особую дату ${index + 1}`"
              @click="store.removeException(index)"
            >
              <q-icon name="delete" />
            </button>
          </div>
          <BranchDayEditor kind="exception" :index="index" title="Режим на эту дату" />
        </div>
        <p v-if="!draft.exceptions.length" class="w-small w-muted">
          Особых дат нет. Действует недельный режим.
        </p>
      </div>
    </section>

    <div class="w-branch-bottom">
      <div
        class="w-branch-feedback"
        :class="feedback && (feedback.result.ok ? 'w-notice' : 'w-error')"
        :role="feedback?.result.ok === false ? 'alert' : 'status'"
        aria-live="polite"
      >
        <template v-if="feedback">
          <span>{{ feedbackText }}</span>
          <p v-if="blockedCount">
            Ячеек без пациента вне нового режима: {{ blockedCount }}.
            Они останутся в календарях и будут недоступны для записи.
          </p>
          <ul v-if="conflicts.length">
            <li v-for="(conflict, index) in conflicts" :key="index">
              {{ conflict.date || conflict.slot?.date || '' }}
              {{ conflict.start || conflict.slot?.start || '' }}
              {{ conflict.message || 'Есть запись пациента за пределами нового режима.' }}
            </li>
          </ul>
        </template>
      </div>
      <div class="w-actions">
        <button class="w-button" type="button" @click="store.previewBranch()">
          Проверить изменения
        </button>
        <button class="w-button primary" type="button" @click="store.saveBranch()">
          Сохранить режим
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  computed, onBeforeUnmount, onMounted, reactive, ref,
} from 'vue';
import { useScheduleStore } from '@/store/calendar/scheduleModule';
import {
  addDays, formatDate, hoursText, isValidDate, WEEKDAY_NAMES,
} from '~utils/calendarModel';
import BranchDayEditor from './BranchDayEditor.vue';

const MAX_CONFLICTS = 8;
const LONG_DATE: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };

const store = useScheduleStore();
const draft = computed(() => store.branchDraft);

// На телефоне дни недели свёрнуты, раскрытые запоминаются
const mobileQuery = window.matchMedia('(max-width: 650px)');
const isMobile = ref(mobileQuery.matches);
const expandedDays = reactive(new Set([0]));

const onMobileChange = (event: MediaQueryListEvent) => {
  isMobile.value = event.matches;
};

const onToggle = (index: number, event: Event) => {
  if (!isMobile.value) return;
  if ((event.target as HTMLDetailsElement).open) expandedDays.add(index);
  else expandedDays.delete(index);
};

// «Черновик · с 12 октября 2026 до 31 декабря 2026 · Europe/Moscow»
const periodText = computed(() => {
  const current = draft.value;
  if (!current) return '';
  const next = store.schedule.branchRules
    .filter((rule) => (
      rule.branchId === store.branchId && rule.effectiveFrom > current.effectiveFrom
    ))
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))[0];
  const from = isValidDate(current.effectiveFrom)
    ? formatDate(current.effectiveFrom, LONG_DATE)
    : 'выбранной даты';
  const until = next
    ? ` до ${formatDate(addDays(next.effectiveFrom, -1), LONG_DATE)}`
    : ' · до следующего изменения';
  const prefix = store.branchDirty ? 'Черновик · ' : '';
  return `${prefix}с ${from}${until} · ${store.currentBranch?.timeZone || 'время филиала'}`;
});

const feedback = computed(() => store.branchFeedback);

const feedbackText = computed(() => {
  const { result, saved } = feedback.value || {};
  if (!result?.ok) return result?.message || '';
  return saved
    ? 'Режим филиала сохранён.'
    : 'Режим проверен. Существующие записи пациентов не затронуты.';
});

const blockedCount = computed(() => feedback.value?.result.blockedSlotIds?.length || 0);

const conflicts = computed(() => (
  (feedback.value?.result.conflicts || []).slice(0, MAX_CONFLICTS)
));

onMounted(() => mobileQuery.addEventListener('change', onMobileChange));
onBeforeUnmount(() => mobileQuery.removeEventListener('change', onMobileChange));
</script>
